import { useState, useRef, useEffect } from "react";
import { Shield, ArrowRight, Loader2 } from "lucide-react";
import { apiClient } from "../services/ApiServices";
// import { verifyOTP } from '../services/mockApi';

export default function OTPVerification({ data, onVerified }) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(30);

    useEffect(() => {
    const interval = setInterval(() => {
      setTimer(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);
  const handleResendOTP = async () => {
    if (timer === 0) {
      setTimer(30);
    try {
      setLoading(true);
      await apiClient.post("/customer/verification/resend-otp", {
        customerId: data?.data.customerId,
      });

    } catch (err) {
      console.error("Failed to resend OTP:", err);
    } finally {
      setLoading(false);
    }}}

  const handleChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }

    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);

    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");

    if (otpValue.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    setLoading(true);
    setError("");

    try {
    const response = await apiClient.post("/customer/verification/verify-otp", {
            customerId: data?.data.customerId,
            otp: otpValue,
          });
          const isValid = response.data.success;


      if (isValid) {
        onVerified();
      } else {
        setError("Invalid OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

    if (loading) {
      return (
        
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
       
      );
    }
  

  return (
 <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-[100dvh] flex items-center justify-center px-4 py-6 sm:px-6 sm:py-12">
  <div className="w-full max-w-md sm:max-w-2xl">

    {/* Header Card */}
    <div className="bg-gradient-to-r from-[#472f92] to-[#6a4bc5] shadow-xl rounded-2xl p-5 sm:p-8 text-white mb-6 transition-all">
      <div className="flex flex-col items-center text-center">

        {/* Icon */}
        <div className="flex items-center justify-center mb-3 sm:mb-4">
          <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
        </div>

        <div className="h-1 w-20 sm:w-24 bg-gradient-to-r from-[#e4aa24] to-[#f2c94c] rounded-full mb-3 sm:mb-4"></div>

        <p className="text-purple-100 text-sm sm:text-lg max-w-sm">
          Please enter your account number to begin the verification process
        </p>

        {/* Decorative dots */}
        <div className="flex mt-4 space-x-2">
          <div className="w-2.5 h-2.5 rounded-full bg-white/50" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#e4aa24]" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/50" />
         
        </div>
      </div>
    </div>
<div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg">
    {/* Content */}

    <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-800 mb-2">
      Verify OTP
    </h2>

    <p className="text-center text-sm sm:text-base text-slate-600 mb-1">
      We've sent a verification code to your registered mobile number
    </p>

    <p className="text-center text-xs sm:text-sm text-slate-500 mb-6">
      mobile: <strong>{data?.data.maskedMobile}</strong>
    </p>

    <form onSubmit={handleSubmit} className="space-y-5">

      {/* OTP Inputs */}
      <div className="flex justify-center gap-2 sm:gap-3">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)} 
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={loading}
            className="w-11 h-12 sm:w-14 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
          />
        ))}
      </div>

      {error && (
        <p className="text-center text-sm text-red-600">
          {error}
        </p>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading || otp.join('').length !== 6}
        className="w-full bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white py-3.5 sm:py-4 rounded-lg font-semibold transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Verifying...
          </>
        ) : (
          <>
            Verify & Continue
            <ArrowRight className="w-5 h-5" />
          </>
        )}
      </button>
    </form>

    {/* Resend */}
    <button
      type="button"
      onClick={handleResendOTP}
      disabled={timer > 0}
      className="w-full mt-4 text-sm sm:text-base text-slate-600 font-medium transition-colors active:text-slate-800"
    >
      Resend OTP {timer > 0 && <span className="ml-1"> in {timer} seconds</span>}
    </button>
  </div>
</div>
</div>

  );
}
